<template>
  <div class="main">
    <div class="header">
      <BackButton />
      <h1>Admin Dashboard</h1>
      <p>System Performance & Globale Stats</p>
    </div>

    <LoadingSpinner v-if="loading" message="Lade globale Statistiken..." />

    <div v-if="!loading && stats" class="dashboard-content">
      <!-- Key Metrics Grid -->
      <div class="metrics-grid">
        <div class="metric-card frosted">
          <div class="icon-bg bg-blue">👥</div>
          <div class="metric-info">
            <span class="label">Total Nutzer</span>
            <span class="value">{{ stats.total_users }}</span>
          </div>
        </div>
        <div class="metric-card frosted">
          <div class="icon-bg bg-green">🏘️</div>
          <div class="metric-info">
            <span class="label">Total Gruppen</span>
            <span class="value">{{ stats.total_pools }}</span>
          </div>
        </div>
        <div class="metric-card frosted">
          <div class="icon-bg bg-orange">🍺</div>
          <div class="metric-info">
            <span class="label">Total Drinks</span>
            <span class="value">{{ stats.total_drinks }}</span>
          </div>
        </div>
        <div class="metric-card frosted">
          <div class="icon-bg bg-purple">💎</div>
          <div class="metric-info">
            <span class="label">Premium</span>
            <span class="value">{{ stats.total_premium }}</span>
          </div>
        </div>
        <div class="metric-card frosted">
          <div class="icon-bg bg-yellow">💰</div>
          <div class="metric-info">
            <span class="label">Umsatz (geschätzt)</span>
            <span class="value">{{ totalRevenue }},00€</span>
          </div>
        </div>
      </div>

      <!-- User Activity -->
      <div class="card frosted">
        <h3>Nutzer-Engagement</h3>
        <div class="activity-bars">
           <div class="activity-item">
             <div class="label-row">
               <p>Login (letzte 30 Tage)</p>
               <span>{{ stats.active_last_30d }} / {{ stats.total_users }}</span>
             </div>
             <div class="progress-bg"><div class="progress-fill" :style="{width: (stats.total_users > 0 ? (stats.active_last_30d / stats.total_users * 100) : 0) + '%'}"></div></div>
           </div>
           <div class="activity-item">
             <div class="label-row">
               <p>Regelmäßige Logger (7 Tage)</p>
               <span>{{ stats.active_loggers_7d }} / {{ stats.total_users }}</span>
             </div>
             <div class="progress-bg"><div class="progress-fill secondary" :style="{width: (stats.total_users > 0 ? (stats.active_loggers_7d / stats.total_users * 100) : 0) + '%'}"></div></div>
           </div>
        </div>
      </div>

      <div class="two-column">
        <!-- Recent Signups -->
        <div class="card frosted scroll-card">
          <h3>Neueste Registrierungen</h3>
          <div class="list-items">
            <div v-for="user in stats.recent_users" :key="user.email" class="list-item">
              <span class="email">{{ user.email }}</span>
              <span class="date">{{ formatDate(user.registered) }}</span>
            </div>
          </div>
        </div>

        <!-- QR Stats -->
        <div class="card frosted scroll-card" v-if="stats.qr_stats && stats.qr_stats.length > 0">
          <h3>QR-Code Scans</h3>
          <div class="list-items">
            <div v-for="qr in stats.qr_stats" :key="qr.qr_id" class="list-item">
              <span class="qr-label">ID: {{ qr.qr_id }}</span>
              <span class="val">{{ qr.scan_count }} Scans</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from "@/services/AuthService.js";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import BackButton from "@/components/BackButton.vue";
import moment from "moment";

export default {
  name: "AdminDashboardView",
  components: {
    LoadingSpinner,
    BackButton
  },
  data() {
    return {
      loading: true,
      stats: null,
    };
  },
  computed: {
    totalRevenue() {
      if (!this.stats?.pool_limit_breakdown) return 0;
      return this.stats.pool_limit_breakdown.reduce((acc, curr) => {
        if (curr.member_limit === 15) return acc + (curr.pool_count * 1);
        if (curr.member_limit === 100) return acc + (curr.pool_count * 5);
        return acc;
      }, 0);
    }
  },
  async mounted() {
    await this.fetchStats();
  },
  methods: {
    async fetchStats() {
      this.loading = true;
      try {
        const response = await AuthService.getGlobalStats();
        if (response.success) {
          this.stats = response.msg;
        }
      } catch (e) {
        console.error("Error fetching admin stats:", e);
        if (e.response && e.response.status === 403) {
          this.$router.push("/");
        }
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      return moment(date).format("DD.MM.YY HH:mm");
    }
  },
};
</script>

<style scoped>
.main {
  padding: 20px;
  padding-bottom: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--background);
  min-height: 100vh;
}

.header {
  width: 100%;
  max-width: 800px;
  text-align: left;
  margin-bottom: 30px;
}

h1 {
  margin: 10px 0 0 0;
  font-size: 2.2rem;
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary-color) 0%, #52b69a 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.header p {
  color: var(--tertiary-color);
  font-weight: 500;
  margin: 5px 0 0 0;
}

.dashboard-content {
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 120px;
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 15px;
  width: 100%;
}

.metric-card {
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  border-radius: var(--radius-m);
  border: 1px solid var(--border-color);
  transition: transform 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-3px);
}

.icon-bg {
  width: 45px;
  height: 45px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.bg-blue { background: rgba(0, 122, 255, 0.1); }
.bg-green { background: rgba(52, 199, 89, 0.1); }
.bg-orange { background: rgba(255, 159, 10, 0.1); }
.bg-purple { background: rgba(175, 82, 222, 0.1); }
.bg-yellow { background: rgba(255, 204, 0, 0.1); }

.metric-info {
  display: flex;
  flex-direction: column;
}

.metric-info .label {
  font-size: 0.8rem;
  color: var(--tertiary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.metric-info .value {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--primary-color);
}

/* Generic Card */
.card {
  padding: 24px;
  border-radius: var(--radius-l);
  border: 1px solid var(--border-color);
}

.card h3 {
  margin: 0 0 20px 0;
  font-size: 1.2rem;
  color: var(--primary-color);
  font-weight: 700;
}

/* Activity Bars */
.activity-bars {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.label-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.label-row p {
  margin: 0;
  font-weight: 600;
  font-size: 0.95rem;
}

.label-row span {
  font-weight: 700;
  color: var(--primary-color);
}

.progress-bg {
  height: 10px;
  background: var(--background-secondary);
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--primary-color);
  border-radius: 10px;
  transition: width 1s ease-out;
}

.progress-fill.secondary {
  background: #34a0a4;
}

/* Column Layout */
.two-column {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.scroll-card {
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

.list-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
}

.list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: var(--background-secondary);
  border-radius: var(--radius-s);
}

.list-item .email {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--primary-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}

.list-item .date, .list-item .val {
  font-size: 0.8rem;
  color: var(--tertiary-color);
  font-weight: 500;
}

.qr-label {
  font-weight: 700;
  font-family: monospace;
  color: var(--primary-color);
}

@media (max-width: 600px) {
  h1 { font-size: 1.8rem; }
  .two-column { grid-template-columns: 1fr; }
}

[data-theme='dark'] .frosted {
  background: rgba(255, 255, 255, 0.03) !important;
}
</style>
